import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AuthService, BreadcrumbService, HomeService, ProductNameService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, ProductType, RenderedOutput, RenderedOutputStatus, RenderedOutputSummary, SituViewWebApiClient
} from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'home-config-list',
  templateUrl: './home-config-list.component.html',
  styleUrls: ['./home-config-list.component.less']
})
export class HomeConfigListComponent extends SituBaseComponent implements OnChanges {
  @Input() product: ProductName | undefined;
  @Input() refreshToken: string | undefined;
  @Input() viewRenderedOutput: RenderedOutputSummary | undefined;
  @Input() compareRenderedOutput: RenderedOutputSummary | undefined;

  @Output() selectedRenderedOutputId: EventEmitter<string> = new EventEmitter();
  @Output() selectedComparisonRenderedOutputId: EventEmitter<string> = new EventEmitter<string>();
  @Output() onIssueSelectClick: EventEmitter<string> = new EventEmitter<string>();

  mostPopular: boolean = false;
  filter: HomeConfigFilter | undefined;
  listType: RenderedOutputStatus = RenderedOutputStatus.Complete;
  productTypeFilter: ProductName | undefined;
  productTypes = ProductName;
  userFilter: string | undefined;
  bugTracking: boolean = false;

  renderedOutputStatus = RenderedOutputStatus;

  completeConfigs: RenderedOutputSummary[] = [];
  inProgressConfigs: RenderedOutputSummary[] = [];
  queuedConfigs: RenderedOutputSummary[] = [];
  failedConfigs: RenderedOutputSummary[] = [];
  filteredHistoryConfigs: RenderedOutputSummary[] = [];

  renderedOutputId: string | undefined;
  comparisonRenderedOutputId: string | undefined;

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private homeService: HomeService,
    private productNameService: ProductNameService) {
      super(authService, stateService)
    }

  protected onSelectedCompanyChange(): void {
    this.loadRenderedOutputs();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.productTypeFilter = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
    this._subscriptions.push(this.homeService.cacheInvalidation$.subscribe(() => {
      this.loadRenderedOutputs();
    }));
    this._subscriptions.push(this.homeService.cacheInvalidation$.subscribe(() => {
      this.loadRenderedOutputs();
    }));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  };

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes.refreshToken && !changes.refreshToken.isFirstChange() && changes.refreshToken.currentValue) {
    //   this.loadRenderedOutputs();
    // }
  }

  select(renderedOutputId: string) {
    this.selectedRenderedOutputId.emit(renderedOutputId);
  }

  selectForComparison(renderedOutputId: string) {
    this.selectedComparisonRenderedOutputId.emit(renderedOutputId)
  }

  onIssueSelect(issueUrl: string) {
    this.onIssueSelectClick.emit(issueUrl);
  }

  setProductFilter(productName: ProductName) {
    this.bugTracking = false;
    this.productTypeFilter = productName;
    this.loadRenderedOutputs();
  }

  toggleUserFilter() {
    if (this.userFilter) {
      this.userFilter = undefined;
    } else {
      this.userFilter = this.authService.getUserId();
    }
    this.loadRenderedOutputs();
  }

  toggleIssueFilter() {
    this.bugTracking = !this.bugTracking;
    if (this.bugTracking) {
      this.userFilter = undefined;
      this.productTypeFilter = this.product === ProductName.Facade ? ProductName.Facade : ProductName.Panorama;
    }
    this.loadRenderedOutputs();
  }

  loadHomeConfig(renderedOutputSummary: RenderedOutputSummary) {
    this.renderedOutputId = renderedOutputSummary.id;
    this.selectedRenderedOutputId.emit(renderedOutputSummary.id);
  }

  getProductColour(productName?: ProductName) {
    return productName !== undefined ? 
    this.productNameService.getProductNameConfiguration(productName)?.productColor :
      this.productNameService.getProductNameConfiguration(this.product)?.productColor;
  }

  loadRenderedOutputs(status: RenderedOutputStatus = RenderedOutputStatus.Complete) {
    this.loading = true;
  
    const request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = 0;
    request.pageSize = 10;
    request.productName = this.product === ProductName.Facade ? ProductName.Facade : this.productTypeFilter ?? ProductName.Panorama;
    request.status = status;
    if (this.userFilter) request.createdBy = this.userFilter;
  
    this.homeService
      .listRenderedOutputs(request)
      .then((renderedOutputs: RenderedOutput[]) => {
        const copiedData = JSON.parse(JSON.stringify(renderedOutputs));
    
        switch (status) {
          case RenderedOutputStatus.Complete:
            this.listType = RenderedOutputStatus.Complete;
            this.completeConfigs = copiedData;
            break;
          case RenderedOutputStatus.Cancelled:
          case RenderedOutputStatus.Failed:
            this.listType = RenderedOutputStatus.Failed;
            this.failedConfigs = copiedData;
            break;
          case RenderedOutputStatus.InProgress:
            this.listType = RenderedOutputStatus.InProgress;
            this.inProgressConfigs = copiedData;
            break;
          case RenderedOutputStatus.Queued:
            this.listType = RenderedOutputStatus.Queued;
            this.queuedConfigs = copiedData;
            break;
        }
      })
      .catch(() => {
        this.showMessage(
          "Sorry! There was an issue at our end retrieving your renders. Please try again."
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }
  

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  getProductName() {
    switch (this.product) {
      case ProductName.Panorama:
      case ProductName.RayTraced:
      case ProductName.RayTracedPlus:
        return "Panorama";
      case ProductName.Facade:
      case ProductName.Insights:
      case ProductName.Plan:
      case ProductName.VR:
        return ProductName[this.product];
    }
  }
}

export class HomeConfigFilter {
  constructor(public type: string, public value: string, public listType: string) {
  }
}